import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, Stagger, StaggerItem, SectionLabel } from "../components/Motion";
import { IMAGES } from "../lib/site";
import { useLightbox, Watermark } from "../components/Lightbox";

const FLOW = [
  { title: "Buyer Requirement", copy: "Your product, specification, volume and destination requirements." },
  { title: "HAQILA Sourcing", copy: "We review and structure your requirement for the Indonesian supply market." },
  { title: "Manufacturer Matching", copy: "We identify selected manufacturers or producers suited to your product." },
  { title: "Specification Verification", copy: "Product information and available documentation are verified for your review." },
  { title: "Commercial Negotiation", copy: "Pricing, packaging and terms are aligned between you and the supplier." },
  { title: "Production", copy: "The manufacturing partner produces to the agreed specification." },
  { title: "Export Coordination", copy: "Documentation, container loading and shipment are coordinated to your destination." },
];

const CRITERIA = [
  "Production Capability",
  "Product Specification",
  "Quality Consistency",
  "Packaging Capability",
  "Export Readiness",
  "Documentation",
  "Commercial Flexibility",
];

const VERIFICATION = [
  { title: "Buyer Requirement", copy: "We start from your exact product, specification and market requirement." },
  { title: "Supplier / Manufacturer Screening", copy: "Potential manufacturing partners are screened against sourcing criteria." },
  { title: "Specification Matching", copy: "Partner capability is matched against your required product specification." },
  { title: "Sample / Product Review", copy: "Samples or product details can be arranged for your evaluation." },
  { title: "COA & Documentation Review", copy: "Available certificates and documentation are reviewed before order confirmation." },
  { title: "Commercial Negotiation", copy: "Price, packaging, payment terms and timeline are aligned on your behalf." },
  { title: "Production Coordination", copy: "We coordinate the production run with the selected manufacturing partner." },
  { title: "Pre-Shipment Coordination", copy: "Documentation, container loading and shipment are coordinated before dispatch." },
];

// "Inside the Supply" gallery — photographed at selected manufacturing partners' facilities.
const PARTNER_FACILITY_PHOTOS = [
  {
    src: IMAGES.partnerCrusherLine,
    alt: "Coconut shell charcoal being processed on a crusher and conveyor line at a manufacturing partner's facility",
    caption: "Coconut shell charcoal processing at a partner production line",
  },
  {
    src: IMAGES.partnerSortingRaw,
    alt: "Workers sorting raw coconut shell charcoal pieces at a manufacturing partner's facility in Indonesia",
    caption: "Raw coconut shell charcoal sorted at the source",
  },
  {
    src: IMAGES.partnerWarehouseSacks,
    alt: "Forklift and stacked sacks of coconut shell charcoal in a manufacturing partner's warehouse",
    caption: "Stock and warehousing at the source",
    noWatermark: true,
  },
  {
    src: IMAGES.partnerPacking,
    alt: "Workers hand-packing and quality-checking coconut shell charcoal briquettes before dispatch",
    caption: "Hand-packing and quality checks before dispatch",
  },
  {
    src: IMAGES.partnerPastePrep,
    alt: "Charcoal paste being prepared and mixed at a manufacturing partner's facility",
    caption: "Charcoal paste preparation — partner facility",
    noWatermark: true,
  },
  {
    src: IMAGES.partnerFormingLine,
    alt: "Briquette forming and extrusion line in operation at a manufacturing partner's facility",
    caption: "Briquette forming line — partner facility",
    noWatermark: true,
  },
  {
    src: IMAGES.partnerSkilledWorkforce,
    alt: "Skilled worker attending the production line at a manufacturing partner's facility",
    caption: "Skilled workforce at our manufacturing partner's line",
  },
  {
    src: IMAGES.partnerCubePacked,
    alt: "Cube coconut shell charcoal briquettes packed to buyer specification at a manufacturing partner's facility",
    caption: "Cube briquettes packed to buyer specification",
    noWatermark: true,
  },
];

export default function Sourcing() {
  const openLightbox = useLightbox();
  const partnerGalleryImages = PARTNER_FACILITY_PHOTOS.map((p) => ({
    src: p.src,
    alt: p.alt,
    caption: p.caption,
    noWatermark: p.noWatermark,
  }));
  return (
    <>
      <SEO />
      <div data-testid="sourcing-page" className="bg-offwhite">
        <section className="pt-40 pb-20 sm:pb-28">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <SectionLabel index="SOURCING" title="How We Source" />
            <Reveal className="mt-8 max-w-4xl">
              <h1 data-testid="sourcing-primary-heading" className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98]">
                Indonesian Sourcing &amp; Export
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-2xl text-base sm:text-lg text-charcoal/65 leading-relaxed">
                Direct access to selected Indonesian manufacturers for <Link to="/products" data-testid="sourcing-products-context-link" className="text-inherit">coconut shell charcoal briquettes</Link>.
                HAQILA is built around specification-based sourcing capability rather than factory ownership.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="bg-charcoal text-offwhite py-20 sm:py-28 tech-grid">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-7">
              <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-snug">
                "We work with selected independent Indonesian manufacturing partners, matching their capabilities
                to international buyer requirements for coconut charcoal sourcing, quality documentation and
                export coordination."
              </p>
            </Reveal>
            <Reveal delay={0.2} className="lg:col-span-5">
              <div
                className="relative overflow-hidden cursor-zoom-in"
                onClick={() =>
                  openLightbox(
                    [{ src: IMAGES.partnerJumboBag, alt: "Forklift lifting a jumbo bag of coconut shell charcoal at a manufacturing partner's facility in Indonesia", caption: "Export-volume handling at our manufacturing partner's facility" }],
                    0
                  )
                }
              >
                <img
                  src={IMAGES.partnerJumboBag}
                  alt="Forklift lifting a jumbo bag of coconut shell charcoal at a manufacturing partner's facility in Indonesia"
                  loading="lazy"
                  data-testid="sourcing-hero-image"
                  className="w-full h-72 sm:h-96 object-cover img-spotlight"
                />
                <span className="absolute bottom-4 left-4 text-[10px] font-bold uppercase tracking-[0.25em] text-offwhite/80 bg-[#111111]/50 backdrop-blur-sm px-3 py-1.5">
                  Export-volume handling at our manufacturing partner's facility
                </span>
                <Watermark />
              </div>
            </Reveal>
          </div>
        </section>

        <section data-testid="sourcing-criteria-section" className="py-20 sm:py-28">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <SectionLabel index="CRITERIA" title="Partner Selection" />
            <Reveal className="mt-6 max-w-2xl">
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Our Sourcing Criteria</h2>
              <p className="mt-4 text-sm text-charcoal/60 leading-relaxed">
                Manufacturing partners are selected and reviewed against clear criteria before being matched with
                buyer requirements.
              </p>
            </Reveal>
            <Stagger className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-charcoal/10 border border-charcoal/10" gap={0.06}>
              {CRITERIA.map((criterion, i) => (
                <StaggerItem key={criterion} className={i === 0 ? "lg:col-span-2" : ""}>
                  <div
                    data-testid={`criterion-${i + 1}`}
                    className="bg-offwhite hover:bg-sand p-7 h-full flex flex-col justify-between gap-8 transition-colors duration-300"
                  >
                    <span className="font-display text-xs font-semibold tracking-[0.3em] text-charcoal/30">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display text-lg font-bold tracking-tight">{criterion}</h3>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        <section data-testid="partner-facilities-section" className="py-20 sm:py-28 bg-sand">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <SectionLabel index="PARTNERS" title="Inside the Supply" />
            <Reveal className="mt-6 max-w-2xl">
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                Inside the Supply — Our Manufacturing Partners
              </h2>
              <p className="mt-4 text-sm text-charcoal/60 leading-relaxed">
                Photographed at the facilities of selected Indonesian manufacturing partners — from raw material
                sorting to export-ready packaging.
              </p>
            </Reveal>
            <Stagger
              data-testid="partner-facilities-gallery"
              className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3"
              gap={0.07}
            >
              {PARTNER_FACILITY_PHOTOS.map((photo, i) => (
                <StaggerItem key={photo.src}>
                  <div
                    data-testid={`partner-facility-photo-${i + 1}`}
                    className="group relative overflow-hidden aspect-[4/5] cursor-zoom-in"
                    onClick={() => openLightbox(partnerGalleryImages, i)}
                  >
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover img-spotlight transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <span className="absolute bottom-0 inset-x-0 text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.15em] sm:tracking-[0.18em] text-offwhite/90 bg-[#111111]/60 backdrop-blur-sm px-2.5 sm:px-3 py-2 sm:py-2.5 leading-snug">
                      {photo.caption}
                    </span>
                    {!photo.noWatermark && <Watermark />}
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        <section data-testid="verification-section" className="bg-charcoal text-offwhite py-20 sm:py-28 tech-grid">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <SectionLabel index="VERIFY" title="How We Verify Supply" dark />
            <Reveal className="mt-6 max-w-3xl">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                How We Verify Supply
              </h2>
              <p className="mt-4 text-sm text-offwhite/55 leading-relaxed">
                HAQILA coordinates and verifies available product and supplier information — transparently, without
                claiming to be the manufacturer.
              </p>
            </Reveal>
            <Stagger className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10" gap={0.05}>
              {VERIFICATION.map((step, i) => (
                <StaggerItem key={step.title}>
                  <div
                    data-testid={`verification-step-${i + 1}`}
                    className="group bg-charcoal hover:bg-white/[0.04] p-7 h-full flex flex-col gap-6 transition-colors duration-300"
                  >
                    <span className="font-display text-4xl font-black tracking-tight text-outline group-hover:text-clay group-hover:[-webkit-text-stroke:0px] transition-colors duration-500">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display text-base font-bold tracking-tight">{step.title}</h3>
                    <p className="text-sm text-offwhite/55 leading-relaxed">{step.copy}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        <section data-testid="sourcing-flow-section" className="py-20 sm:py-28">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <SectionLabel index="FLOW" title="Sourcing Flow" />
              <Reveal className="mt-6">
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                  From Your Requirement to Export
                </h2>
                <p className="mt-4 text-sm text-charcoal/60 leading-relaxed">
                  A transparent chain of custody — you always know where your product comes from and who stands behind
                  each step.
                </p>
              </Reveal>
              <Reveal delay={0.2} className="mt-8">
                <Link
                  to="/request-a-quote"
                  data-testid="sourcing-requirements-cta"
                  className="group inline-flex items-center gap-2 bg-forest hover:bg-moss text-offwhite font-semibold px-7 py-4 text-sm tracking-wide transition-colors duration-300"
                >
                  Discuss Your Requirements
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Reveal>
            </div>
            <Stagger className="lg:col-span-7" gap={0.08}>
              {FLOW.map((step, i) => (
                <StaggerItem key={step.title}>
                  <div data-testid={`sourcing-step-${i + 1}`}>
                    <div className="flex gap-6 items-start py-6 border-t border-charcoal/10">
                      <span className="font-display text-3xl font-black tracking-tight text-outline-dark shrink-0 w-14">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <h3 className="font-display text-lg font-bold tracking-tight">{step.title}</h3>
                        <p className="mt-1.5 text-sm text-charcoal/60 leading-relaxed">{step.copy}</p>
                      </div>
                    </div>
                    {i < FLOW.length - 1 && <ArrowDown className="w-4 h-4 text-charcoal/25 ml-5 my-1" />}
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      </div>
    </>
  );
}
